import React from 'react';

import PropTypes from 'prop-types';

import { ConfirmationModal } from '@src/components/ui/Modal';

/**
 * PopoverConfirm Component
 *
 * A wrapper component that triggers a confirmation modal when a child element is clicked.
 * Now uses the shared ConfirmationModal component for consistency.
 *
 * Usage:
 * ```jsx
 * <PopoverConfirm
 *   confirmText="Are you sure you want to delete this item?"
 *   onConfirm={handleDelete}
 *   onCancel={() => {}}
 * >
 *   <button>Delete</button>
 * </PopoverConfirm>
 * ```
 *
 * @param {object} props
 * @param {string} props.confirmText - Confirmation message to display
 * @param {node} props.children - Child element that triggers the modal
 * @param {function} props.onConfirm - Confirmation handler
 * @param {function} props.onCancel - Cancel handler
 */
const PopoverConfirm = ({
    confirmText = 'Are you sure?',
    children,
    onConfirm = () => {},
    onCancel = () => {},
}) => {
    const [show, setShow] = React.useState(false);
    const target = React.useRef(null);

    const handleOnClick = React.useCallback((e) => {
        e.preventDefault();
        setShow(true);
    }, []);

    const handleCancel = React.useCallback(() => {
        setShow(false);
        onCancel();
    }, [onCancel]);

    const handleConfirm = React.useCallback(() => {
        setShow(false);
        onConfirm();
    }, [onConfirm]);

    return (
        <>
            {React.cloneElement(
                React.Children.only(children),
                { onClick: handleOnClick, ref: target },
            )}
            <ConfirmationModal
                centered
                show={show}
                title="Confirm Deletion"
                message={confirmText}
                confirmText="Yes, Delete"
                cancelText="Cancel"
                variant="danger"
                onConfirm={handleConfirm}
                onCancel={handleCancel}
            />
        </>
    );
};

PopoverConfirm.propTypes = {
    children: PropTypes.node.isRequired,
    confirmText: PropTypes.string,
    onConfirm: PropTypes.func,
    onCancel: PropTypes.func,
};

export default React.memo(PopoverConfirm);
